package com.bulut.mecitogullari.ui.screens.craftsmen

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.nowEpochMillis
import com.bulut.mecitogullari.core.util.parseDecimalAmount
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.Craftsman
import com.bulut.mecitogullari.data.model.CraftsmanBalance
import com.bulut.mecitogullari.data.model.CraftsmanEntry
import com.bulut.mecitogullari.data.model.WorkTypeAgreement
import com.bulut.mecitogullari.data.model.craftsmanBalance
import com.bulut.mecitogullari.data.repository.BarterRepository
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.UnitRepository
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

data class CraftsmanCardData(
    val craftsman: Craftsman,
    val balance: CraftsmanBalance,
    val unitsGiven: List<ApartmentUnit>,
    val entries: List<CraftsmanEntry>,
) {
    val unitsGivenCount: Int get() = unitsGiven.size
}

data class CraftsmenUiState(
    val craftsmen: List<CraftsmanCardData> = emptyList(),
    /** Project id → name, for the printable statement. */
    val projectNames: Map<String, String> = emptyMap(),
)

class CraftsmenViewModel(
    private val barterRepository: BarterRepository,
    unitRepository: UnitRepository,
    projectRepository: ProjectRepository,
) : ViewModel() {

    val uiState: StateFlow<CraftsmenUiState> = combine(
        barterRepository.craftsmen,
        barterRepository.entries,
        unitRepository.units,
        projectRepository.projects,
    ) { craftsmen, entries, units, projects ->
        CraftsmenUiState(
            craftsmen = craftsmen.map { craftsman ->
                val given = units.filter { it.barterCraftsmanId == craftsman.id }
                val ownEntries = entries.filter { it.craftsmanId == craftsman.id }
                CraftsmanCardData(
                    craftsman = craftsman,
                    balance = craftsmanBalance(
                        craftsman = craftsman,
                        entries = ownEntries,
                        unitsGiven = given,
                    ),
                    unitsGiven = given.sortedBy { it.number },
                    entries = ownEntries,
                )
            }.sortedBy { it.craftsman.name.lowercase() },
            projectNames = projects.associate { it.id to it.name },
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = CraftsmenUiState(),
    )

    fun createCraftsman(
        name: String,
        trade: String,
        phone: String,
        notes: String,
        workTypes: List<WorkTypeInput>,
        requiredInvoiceText: String,
    ) {
        if (name.isBlank()) return
        viewModelScope.launch {
            barterRepository.upsertCraftsman(
                Craftsman(
                    id = newId(),
                    name = name.trim(),
                    trade = trade.trim(),
                    phone = phone.trim(),
                    notes = notes.trim(),
                    requiredInvoiceAmount = requiredInvoiceText.toLongOrNull()?.toDouble() ?: 0.0,
                    workTypes = workTypes.mapNotNull { input ->
                        val count = parseDecimalAmount(input.countText) ?: 0.0
                        val price = input.priceText.toLongOrNull()?.toDouble() ?: 0.0
                        if (count > 0) {
                            WorkTypeAgreement(type = input.type, agreedCount = count, unitPrice = price)
                        } else {
                            null
                        }
                    },
                    createdAtEpochMillis = nowEpochMillis(),
                )
            )
        }
    }
}

/** One row of the add-craftsman form: 1+1 / 2+1 / 3+1. */
data class WorkTypeInput(
    val type: String,
    val countText: String = "",
    val priceText: String = "",
)
